import { useEffect } from "react";
import type { Href } from "expo-router";
import { useLocalSearchParams } from "expo-router";
import { useFleetSignal, useTenantSignal, type SignalDetail } from "@visioni/a3s-monitoring/app";
import { useReplaceTenantRoute } from "@src/features/tenants";
import { TENANT_SIGNALS_SUBPATH, activationHref, fleetClientHref, patriotToolsHref, signalHref } from "../routes";
import { useActivationScopeOptions } from "../hooks/useActivationScopeOptions";
import { useDirectoryTenantName } from "../hooks/useDirectoryTenantName";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { SignalDetailView } from "../components/SignalDetailView";
import { EMPTY, formatPreciseInstant, hasText } from "../format";

const rawId = (id: string) => id;

const subtitleOf = (signal: SignalDetail) =>
  [signal.description, `received ${formatPreciseInstant(signal.receivedDateTime)}`].filter(hasText).join(" · ");

export function TenantSignalScreen() {
  const { tenantId, signalId = "" } = useLocalSearchParams<{ tenantId: string; signalId?: string }>();
  const { signal, isLoading, error, refetch } = useTenantSignal(signalId);
  const scope = useActivationScopeOptions();
  const tenantName = useDirectoryTenantName();

  return (
    <PatriotToolsPage
      title={`Patriot signal ${signalId}`}
      {...(signal !== undefined && { subtitle: subtitleOf(signal) })}
    >
      {signal === undefined ?
        <QueryNotice isLoading={isLoading} error={error} onRetry={refetch} />
      : <SignalDetailView
          signal={signal}
          // The route serves only a signal whose marker names this tenant.
          tenant={{ label: tenantName(tenantId) ?? tenantId, href: patriotToolsHref(tenantId, TENANT_SIGNALS_SUBPATH) }}
          clientHref={(clientNo) => fleetClientHref(tenantId, clientNo)}
          activationHref={(ticketNo) => activationHref(tenantId, ticketNo, tenantId)}
          siteLabel={scope.siteLabel}
          cameraLabel={scope.cameraLabel}
          cameraHref={(siteId, cameraKey) => `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}` as Href}
          listHref={(filters) => patriotToolsHref(tenantId, TENANT_SIGNALS_SUBPATH, filters)}
        />
      }
    </PatriotToolsPage>
  );
}

/**
 * One signal by Patriot's id alone, for one whose tenant the directory does not
 * hold, or that has none. A signal whose tenant it does hold is redirected to
 * that tenant's own page, as a fleet activation is.
 */
export function FleetSignalScreen() {
  const replaceRoute = useReplaceTenantRoute();
  const { tenantId, signalId = "" } = useLocalSearchParams<{ tenantId: string; signalId?: string }>();
  const { signal, isLoading, error, refetch } = useFleetSignal(signalId);
  const tenantName = useDirectoryTenantName();

  const owner = signal?.a3sTenant;
  const known = owner !== undefined && tenantName(owner) !== undefined ? owner : undefined;

  useEffect(() => {
    if (known !== undefined) replaceRoute(signalHref(tenantId, signalId, known));
  }, [known, tenantId, signalId, replaceRoute]);

  return (
    <PatriotToolsPage
      title={`Patriot signal ${signalId}`}
      {...(signal !== undefined && { subtitle: subtitleOf(signal) })}
    >
      {signal === undefined || known !== undefined ?
        <QueryNotice isLoading={isLoading || known !== undefined} error={error} onRetry={refetch} />
      : <SignalDetailView
          signal={signal}
          tenant={{ label: owner ?? EMPTY }}
          clientHref={(clientNo) => fleetClientHref(tenantId, clientNo)}
          activationHref={(ticketNo) => activationHref(tenantId, ticketNo, undefined)}
          siteLabel={rawId}
          cameraLabel={rawId}
        />
      }
    </PatriotToolsPage>
  );
}
